import { useState } from "react";
import { useTranslation } from "react-i18next";

import { canControlRun, isRunTerminal, type CcRunState } from "@/api/code-companion";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useCurrentUser } from "@/hooks/useAuth";
import { useCcCreateRun } from "@/hooks/useCodeCompanion";

/**
 * Disparar o run autônomo (S-060 F1/F6/F7, movido para o hero na onda 7).
 *
 * Estava embutido no `Sheet` do pacote. Quando o Trace virou destino, o botão foi junto
 * — e o guarda-corpo de i18n foi quem pegou: a chave `Start development` sumiu das telas,
 * e sumir dali significava que **não havia mais como iniciar um run**. Guarda que checa
 * a existência de uma chave pegou a ausência de uma funcionalidade.
 *
 * Três regras que ele carrega:
 *
 * **Volta quando o run termina.** Sem isso, depois do primeiro run o pacote nunca mais
 * mostra como disparar outro, mesmo com o backend aceitando.
 *
 * **Quem não pode, não vê.** Disparar exige `consultant`; o backend recusa com 403, e
 * oferecer a ação seria prometer o que não vai acontecer.
 *
 * **Confirmação com texto, não `window.confirm`.** A ação escreve ABAP no SAP do cliente
 * usando a sessão ADT de quem clicou, e o confirm nativo não tem onde dizer isso — nem
 * estilo, nem i18n, e some no modo quiosque de alguns browsers.
 */
export default function StartRunButton({
  projectId,
  packageId,
  changeSetId,
  run,
}: {
  projectId: string;
  packageId: number;
  /**
   * O change set que a tela está exibindo.
   *
   * Sem isto o backend escolhe o mais novo por conta própria, e desde que o Trace passou
   * a abrir no mais novo **com objeto** os dois podiam divergir: a matriz mostrando o
   * CS-9 e o botão disparando sobre o CS-28. O mesmo botão, na mesma tela, agindo sobre
   * outro assunto.
   */
  changeSetId?: number | null;
  run?: CcRunState | null;
}) {
  const { t } = useTranslation();
  const [aberto, setAberto] = useState(false);
  const createRun = useCcCreateRun(projectId, packageId);
  const podeControlar = canControlRun(useCurrentUser()?.role);

  if (!podeControlar) return null;
  if (run && !isRunTerminal(run.status)) return null;

  return (
    <div className="flex flex-col items-end gap-1">
      <AlertDialog open={aberto} onOpenChange={setAberto}>
        <AlertDialogTrigger asChild>
          <button
            type="button"
            data-testid="start-run"
            disabled={createRun.isPending}
            className="flex h-8 items-center gap-2 rounded-md px-3 text-xs font-semibold disabled:opacity-50"
            style={{
              background: "linear-gradient(135deg,var(--exed-gold),var(--exed-gold-deep))",
              color: "var(--exed-navy)",
              border: 0,
              boxShadow: "0 4px 12px rgba(255,200,61,.3)",
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <path d="m5 3 14 9-14 9V3z" />
            </svg>
            {createRun.isPending ? t("Starting...") : t("Start development")}
          </button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("Start the autonomous migration of this package?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {/* CC.34 F2: o texto passou a descrever o que o botão faz de verdade. Ele
                  prometia deploy e ativação, e o drafting — a fase mais longa, uma
                  chamada ao modelo por objeto — ficava de fora, junto com o custo dela e
                  com o fato de que parte dos objetos vai para o modo assistido. */}
              {t(
                "The run reads the legacy objects from the ECC, transforms them into clean-core ABAP, then deploys and activates them on the target SAP using your own ADT session. Objects the target refuses go to assisted mode instead of failing. It pauses on its own if the session drops or a decision is needed, and you can pause or cancel at any point.",
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("Cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => createRun.mutate(changeSetId ? { change_set_id: changeSetId } : undefined)}>
              {t("Start development")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* O backend recusa com motivo acionável (ex.: sem change set aberto). Mostrar o
          motivo é o que evita o "não funcionou". */}
      {createRun.isError && (
        <span data-testid="start-run-error" className="max-w-[36ch] text-right text-[11px] text-red-200">
          {(createRun.error as { response?: { data?: { detail?: string } } })?.response?.data
            ?.detail ?? t("Could not start the run.")}
        </span>
      )}
    </div>
  );
}
